import { useContext, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import AuthContext from "../context/AuthContext";
import Loading from "./Loading";
import { planForBillingTier } from "../config/plans";

const planName = (tier) => planForBillingTier(tier)?.name || null;

const ROLE_NAMES = {
    admin: "Admin",
    rm: "Relationship Manager",
    compliance: "Compliance",
    head: "Head of Advisory",
    portfolio_adviser: "Portfolio Adviser",
    view_only: "View Only",
};

/** Why the page is closed to this user, in words they can act on. */
export const accessExplanation = (user, allowedRoles, allowedSubscriptions) => {
    if (allowedSubscriptions && !allowedSubscriptions.includes(user.subscription)) {
        const included = allowedSubscriptions.map(planName).filter(Boolean);
        const current = planName(user.subscription);
        return {
            title: "Not included in your plan",
            description: `This page is part of ${included.join(" and ") || "a paid plan"}${current ? `; your workspace is on ${current}` : ""}. You can compare plans on the Subscription page.`,
        };
    }
    const role = ROLE_NAMES[user.role] || user.role;
    return {
        title: "Not available for your role",
        description: `Your role${role ? ` (${role})` : ""} does not include this page. Ask your workspace admin if you need it.`,
    };
};

const ExplainedRedirect = ({ to, from, explanation }) => {
    const toast = useToast();
    useEffect(() => {
        const id = `access-${from}`;
        if (!toast.isActive(id)) {
            toast({ id, status: "info", duration: 8000, isClosable: true, ...explanation });
        }
    }, [toast, from, explanation]);
    return <Navigate to={to} replace state={{ from, blocked: explanation.title }} />;
};

const PrivateRoute = ({
    children,
    allowedRoles,
    allowedSubscriptions,
    redirectTo = "/dashboard",
}) => {
    const { token, user, isAuthInitializing } = useContext(AuthContext);
    const location = useLocation();

    if (isAuthInitializing) {
        return <Loading />;
    }

    if (!(token && user)) {
        return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }

    const hasAllowedRole = !allowedRoles || allowedRoles.includes(user.role);
    const hasAllowedSubscription = !allowedSubscriptions || allowedSubscriptions.includes(user.subscription);

    if (!hasAllowedRole || !hasAllowedSubscription) {
        return (
            <ExplainedRedirect
                to={redirectTo}
                from={location.pathname}
                explanation={accessExplanation(user, hasAllowedRole ? null : allowedRoles, hasAllowedSubscription ? null : allowedSubscriptions)}
            />
        );
    }

    return children;
};

export default PrivateRoute;
